/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { fetchSites, fetchLastBookedSites, fetchSiteDetail, updateTwoManSiteNotes, uploadSiteImage, updateSiteImage, deleteSiteImage } from './sitesApi';
import { SitesState, SitesDataProps, RecentVisitRequest, SiteDetail, SiteImage } from './types';
import { getErrorMessage } from '../utils/helpers';

const initialState: SitesState = {
    error: null,
    isFetching: false,
    isFetchingLastBooked: false,
    isFetchingSiteDetail: false,
    sitesData: [],
    recentRequests: [],
    pagination: {
        currentPage: 1,
        pageSize: 12,
        total: 0,
    },
    filterData: {
        sites: [],
        siteTypes: [],
    },
    siteDetail: null,
    siteDetailError: null,
};

export const sitesSlice = createSlice({
    name: 'sites',
    initialState,
    reducers: {
        resetSitesPagination: (state: SitesState) => {
            state.pagination = {
                currentPage: 1,
                pageSize: 12,
                total: 0,
            };
        },
        clearSiteDetail: (state: SitesState) => {
            state.siteDetail = null;
            state.siteDetailError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSites.pending, (state: SitesState) => {
                state.isFetching = true;
                state.error = null;
            })
            .addCase(fetchSites.fulfilled, (
                state: SitesState,
                action: PayloadAction<SitesDataProps>,
            ) => {
                state.isFetching = false;
                if (action.payload.sites) {
                    state.sitesData = action.payload.sites.data;
                    state.pagination = {
                        currentPage: action.payload.sites.meta.current_page || 1,
                        pageSize: action.payload.sites.meta.per_page || 12,
                        total: action.payload.sites.meta.total || 0,
                    };
                } else {
                    state.sitesData = [];
                }
                if (action.payload.filters) {
                    state.filterData = action.payload.filters;
                }
            })
            .addCase(fetchSites.rejected, (state: SitesState, action) => {
                state.isFetching = false;
                state.error = getErrorMessage(action.error as SerializedError, 'Failed to load sites');
            })
            .addCase(fetchLastBookedSites.pending, (state: SitesState) => {
                state.isFetchingLastBooked = true;
            })
            .addCase(fetchLastBookedSites.fulfilled, (
                state: SitesState,
                action: PayloadAction<{ requests: RecentVisitRequest[] }>,
            ) => {
                state.isFetchingLastBooked = false;
                state.recentRequests = action.payload.requests || [];
            })
            .addCase(fetchLastBookedSites.rejected, (state: SitesState) => {
                state.isFetchingLastBooked = false;
            })
            .addCase(fetchSiteDetail.pending, (state: SitesState) => {
                state.isFetchingSiteDetail = true;
                state.siteDetailError = null;
            })
            .addCase(fetchSiteDetail.fulfilled, (
                state: SitesState,
                action: PayloadAction<{ site: SiteDetail }>,
            ) => {
                state.isFetchingSiteDetail = false;
                state.siteDetail = action.payload.site;
            })
            .addCase(fetchSiteDetail.rejected, (state: SitesState, action) => {
                state.isFetchingSiteDetail = false;
                state.siteDetailError = getErrorMessage(
                    action.error as SerializedError,
                    'Failed to load site details',
                );
            })
            .addCase(updateTwoManSiteNotes.fulfilled, (state, action) => {
                if (state.siteDetail?.siteInfo) {
                    state.siteDetail.siteInfo.twoManSiteNotes = action.payload.twoManSiteNotes;
                }
            })
            .addCase(uploadSiteImage.fulfilled, (
                state,
                action: PayloadAction<{ siteImage: SiteImage }>,
            ) => {
                if (state.siteDetail) {
                    if (!state.siteDetail.siteImages) {
                        state.siteDetail.siteImages = [];
                    }
                    state.siteDetail.siteImages.push(action.payload.siteImage);
                }
            })
            .addCase(updateSiteImage.fulfilled, (
                state,
                action: PayloadAction<{ siteImage: { id: number; description: string | null } }>,
            ) => {
                if (state.siteDetail?.siteImages) {
                    const image = state.siteDetail.siteImages.find(
                        (img) => img.id === action.payload.siteImage.id,
                    );
                    if (image) {
                        image.description = action.payload.siteImage.description;
                    }
                }
            })
            .addCase(deleteSiteImage.fulfilled, (
                state,
                action: PayloadAction<{ imageId: number }>,
            ) => {
                if (state.siteDetail?.siteImages) {
                    state.siteDetail.siteImages = state.siteDetail.siteImages.filter(
                        (img) => img.id !== action.payload.imageId,
                    );
                }
            });
    },
});

export default sitesSlice.reducer;
export const { resetSitesPagination, clearSiteDetail } = sitesSlice.actions;
